import { NgClass, NgStyle } from '@angular/common';
import { Component, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BADGE_STATUS_COLORS, BadgeDirective, BadgePosition, BadgeSize } from '@ra-web-tech-ui-toolkit/components';
import { ICellRendererParams } from 'ag-grid-community';
import { ENTITLEMENT_NEW_THRESHOLD_DAYS } from '../../../common-constants';
import { CustomDatePipe } from '../../../pipes/date/custom-date.pipe';

/**
 * Params accepted by the shared trial details renderer. Each consuming application
 * configures these flags/fields rather than the renderer depending on app-specific
 * row shapes or modules.
 */
export interface AgTrialDetailsRendererParams extends ICellRendererParams {
  /** Row field holding the title text shown in the bold header line. */
  titleField: string;
  /** Extra class applied to the title container (for app-specific spacing). */
  titleContainerClass?: string;
  /** Show a "new" badge next to the title when the row was recently redeemed. */
  highlightNewItems?: boolean;
  /** Show "Allocated on"/"Allocated by" plain-text details (Home UI style). */
  showAllocationDetails?: boolean;
  /** Show a linked "Allocated By"/"Allocated On" details block (Common Admin style). */
  showAllocatedByLink?: boolean;
  /** Router link array used for the allocator link, e.g. ['/users']. The row's
   * `allocatedByUserId` is appended as the final segment. */
  allocatedByRouterLinkBase?: string[];
}

@Component({
  selector: 'app-ag-trial-details',
  templateUrl: './ag-trial-details.component.html',
  styleUrls: ['./ag-trial-details.component.scss'],
  imports: [BadgeDirective, NgClass, NgStyle, RouterModule, CustomDatePipe],
})
export class AgTrialDetailsComponent {
  BADGE_STATUS_COLORS = BADGE_STATUS_COLORS;
  BadgePosition = BadgePosition;
  BadgeSize = BadgeSize;

  titleField = '';
  titleContainerClass = '';
  highlightNewItems = false;
  showAllocationDetails = false;
  showAllocatedByLink = false;
  allocatedByRouterLinkBase: string[] = ['/users'];
  toggle = false;
  greyOutText = false;

  /**
   * Supports two usage modes: as a direct AG Grid cell renderer (via `agInit`), and
   * as an embedded component configured by a compatibility wrapper (via this `@Input`).
   */
  @Input()
  set params(params: AgTrialDetailsRendererParams) {
    this.applyParams(params);
  }
  get params(): AgTrialDetailsRendererParams {
    return this._params;
  }
  private _params!: AgTrialDetailsRendererParams;

  agInit(params: AgTrialDetailsRendererParams): void {
    this.applyParams(params);
  }

  private applyParams(params: AgTrialDetailsRendererParams): void {
    this._params = params;
    this.titleField = params.titleField;
    this.titleContainerClass = params.titleContainerClass ?? '';
    this.highlightNewItems = params.highlightNewItems ?? false;
    this.showAllocationDetails = params.showAllocationDetails ?? false;
    this.showAllocatedByLink = params.showAllocatedByLink ?? false;
    this.allocatedByRouterLinkBase = params.allocatedByRouterLinkBase ?? ['/users'];

    const status = params.data?.status?.toLowerCase?.();
    this.greyOutText = status === 'expired' || status === 'ended';
  }

  refresh(): boolean {
    return false;
  }

  get title(): string {
    return this.params?.data?.[this.titleField];
  }

  get allocatedByRouterLink(): string[] {
    return [...this.allocatedByRouterLinkBase, this.params?.data?.allocatedByUserId];
  }

  checkIsNew(): boolean {
    if (!this.highlightNewItems) {
      return false;
    }
    const currentDate = new Date();
    const data = this.params.data;
    const redeemedOn = new Date(data.redeemedOn);
    const daysSinceRedeemed = (currentDate.getTime() - redeemedOn.getTime()) / (3600 * 24 * 1000);
    return daysSinceRedeemed <= ENTITLEMENT_NEW_THRESHOLD_DAYS;
  }

  toggleDetails(): void {
    this.toggle = !this.toggle;
  }
}
